import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AUTH_EVENT_NAME,
  beginSignIn,
  canAccessModule as canAccessModuleForProfile,
  endAccessSession,
  getRuntimeAccessProfile,
  hasPermission as hasPermissionForProfile,
  restoreAccessSession,
  retrySignIn,
} from './accessControl';
import { subscribeWorkflowChannel } from './app-state/crossFeatureWorkflowExternalState';

/** @typedef {{
 * accessProfile: ReturnType<typeof getRuntimeAccessProfile>,
 * user: ReturnType<typeof getRuntimeAccessProfile>,
 * refreshAccessProfile: () => void,
 * signIn: () => void,
 * signOut: () => void, sessionRestoring: boolean, signInBlocked: boolean,
 * hasPermission: (permission: string) => boolean,
 * canAccessModule: (module: Parameters<typeof canAccessModuleForProfile>[0]) => boolean
 * }} AuthContextValue */
/** @type {React.Context<AuthContextValue>} */
const AuthContext = createContext(/** @type {AuthContextValue} */ ({
  accessProfile: getRuntimeAccessProfile(),
  user: getRuntimeAccessProfile(),
  refreshAccessProfile: () => {},
  signIn: () => {},
  signOut: () => {},
  sessionRestoring: true,
  signInBlocked: false,
  hasPermission: () => false,
  canAccessModule: () => false,
}));

export function AuthProvider({ children }) {
  const [version, setVersion] = useState(0);
  const [sessionRestoring, setSessionRestoring] = useState(typeof window !== 'undefined');
  const [signInBlocked, setSignInBlocked] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const refreshAccessProfile = useCallback(() => {
    setVersion((current) => current + 1);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleAuthUpdate = () => refreshAccessProfile();
    const handleVisibility = () => {
      try {
        if (document.visibilityState === 'visible') {
          refreshAccessProfile();
        }
      } catch {}
    };

    const unsubscribeAuth = subscribeWorkflowChannel(AUTH_EVENT_NAME, handleAuthUpdate);
    window.addEventListener('focus', handleAuthUpdate);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      unsubscribeAuth();
      window.removeEventListener('focus', handleAuthUpdate);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [refreshAccessProfile]);

  const accessProfile = useMemo(() => getRuntimeAccessProfile(), [version]);

  // Finish a returning Keycloak sign-in, or restore the tab's stored session.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let cancelled = false;
    void restoreAccessSession().catch(() => null).finally(() => {
      if (!cancelled) {
        setSessionRestoring(false);
        refreshAccessProfile();
      }
    });
    return () => { cancelled = true; };
  }, [refreshAccessProfile]);

  // No usable session: go to the Keycloak login page (the loop guard stops runaway redirects).
  // A signed-in user without a Mitus role is not redirected: SSO would sign them straight back in.
  useEffect(() => {
    if (sessionRestoring || signingOut || signInBlocked || accessProfile.sessionActive || accessProfile.roleMissing) return;
    if (!beginSignIn(() => setSignInBlocked(true))) setSignInBlocked(true);
  }, [accessProfile.roleMissing, accessProfile.sessionActive, sessionRestoring, signInBlocked, signingOut]);

  const signIn = useCallback(() => {
    setSignInBlocked(false);
    if (!retrySignIn(() => setSignInBlocked(true))) setSignInBlocked(true);
  }, []);

  const signOut = useCallback(() => {
    setSigningOut(true);
    void endAccessSession().catch(() => {
      setSigningOut(false);
      refreshAccessProfile();
    });
  }, [refreshAccessProfile]);

  const value = useMemo(() => ({
    accessProfile,
    user: accessProfile,
    refreshAccessProfile,
    signIn,
    signOut,
    sessionRestoring,
    signInBlocked,
    hasPermission: (permission) => hasPermissionForProfile(permission, accessProfile),
    canAccessModule: (module) => canAccessModuleForProfile(module, accessProfile),
  }), [accessProfile, refreshAccessProfile, sessionRestoring, signIn, signInBlocked, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
